import { faHubspot, faMicrosoft, faSalesforce } from "@fortawesome/free-brands-svg-icons";
import { faLink, IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import { FunctionComponent } from "react";

import ZohoIcon from "@assets/icons/zoho.png";
import { ReactComponent as PipedriveIcon } from "@assets/icons/pipedrive.svg";

import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { Icon } from "@/components";

interface CrmIconProps {
	url: string | null;
	crmType?: string | null;
	hasDeal?: boolean;
	syncStatus?: string | null;
	syncError?: string | null;
}

const getCrmKey = (url: string | null, crmType?: string | null) => {
	const source = `${url ?? ""} ${crmType ?? ""}`.toLowerCase();

	if (source.includes("hubspot")) {
		return "hubspot";
	}

	if (source.includes("zoho")) {
		return "zoho";
	}

	if (source.includes("pipedrive")) {
		return "pipedrive";
	}

	// Matches both the `MSDYNAMICS` crmType and a `…crm4.dynamics.com/main.aspx` deep link.
	if (source.includes("dynamics")) {
		return "msdynamics";
	}

	if (source.includes("salesforce")) {
		return "salesforce";
	}

	return url || crmType ? "generic" : null;
};

/**
 * Whether CrmIcon will render anything for this record — it resolves to an empty
 * fragment when url/crmType/syncStatus/syncError are all empty. Use this to skip
 * records (and their containers) that would otherwise render as empty space.
 */
export const rendersCrmIcon = (
	record: Pick<CrmIconProps, "url" | "crmType" | "syncStatus" | "syncError">
): boolean =>
	Boolean(
		getCrmKey(record.url, record.crmType) ??
		(record.syncStatus || record.syncError ? "generic" : null)
	);

const StatusIndicator: FunctionComponent<
	Pick<CrmIconProps, "hasDeal" | "syncStatus" | "syncError">
> = ({ hasDeal, syncStatus, syncError }) => {
	if (syncStatus === "PENDING") {
		return (
			<span
				aria-label="CRM sync pending"
				className="absolute -top-0.5 -right-0.5 block h-2.5 w-2.5 animate-spin rounded-full border-2 border-border-light border-t-blue-action"
			/>
		);
	}

	if (syncError || syncStatus === "ERROR") {
		return (
			<span
				aria-label="CRM sync failed"
				className="absolute top-0.5 -right-0.5 block h-2.5 w-2.5 rounded-full border border-brand-white bg-status-error"
			/>
		);
	}

	return hasDeal ? (
		<span className="absolute top-0.5 -right-0.5 block h-2.5 w-2.5 rounded-full bg-green-500 border border-white" />
	) : null;
};

export const CrmIcon: FunctionComponent<CrmIconProps> = ({
	url,
	crmType,
	hasDeal,
	syncStatus,
	syncError,
}) => {
	const crmKey = getCrmKey(url, crmType) ?? (syncStatus || syncError ? "generic" : null);
	const statusIndicator = (
		<StatusIndicator hasDeal={hasDeal} syncError={syncError} syncStatus={syncStatus} />
	);

	if (crmKey === "hubspot") {
		return (
			<div className="relative mr-1 w-[20px] flex items-center h-[20px]">
				<BrandsIcon className="mr-1 text-[#f57722]" icon={faHubspot as IconDefinition} />
				{statusIndicator}
			</div>
		);
	}

	if (crmKey === "zoho") {
		return (
			<div className="relative mr-1 w-[20px] flex items-center h-[20px]">
				<img alt="Zoho" className="flex mr-1 !h-[18px] !w-[18px] !min-w-[18px]" src={ZohoIcon} />
				{statusIndicator}
			</div>
		);
	}

	if (crmKey === "pipedrive") {
		return (
			<div className="relative mr-1 w-[20px] flex items-center h-[20px]">
				<PipedriveIcon
					aria-label="Pipedrive"
					className="flex mr-1 !h-[18px] !w-[18px] !min-w-[18px]"
				/>
				{statusIndicator}
			</div>
		);
	}

	if (crmKey === "msdynamics") {
		return (
			<div className="relative mr-1 w-[20px] flex items-center h-[20px]">
				<BrandsIcon className="mr-1 text-[#0078d4]" icon={faMicrosoft as IconDefinition} />
				{statusIndicator}
			</div>
		);
	}

	if (crmKey === "salesforce") {
		return (
			<div className="relative mr-1 w-[20px] flex items-center h-[20px]">
				<BrandsIcon className="mr-1 text-[#00A1E0]" icon={faSalesforce as IconDefinition} />
				{statusIndicator}
			</div>
		);
	}

	if (crmKey === "generic") {
		return (
			<div className="relative mr-1 w-[20px] flex items-center h-[20px]">
				<Icon className="mr-1 text-text-meta" icon={faLink} />
				{statusIndicator}
			</div>
		);
	}

	return <></>;
};
